import styled from 'styled-components'
import ReactPixel from 'react-facebook-pixel'
import React, { useEffect, useRef, useState, } from 'react'
import { useMediaQuery } from 'react-responsive'
import ImageViewer from 'react-simple-image-viewer'

import { Portfolio, } from 'globals/types'

import { SectionCommonProps } from '../types'

interface Props extends SectionCommonProps {

}

const Section3Container = styled.div`
  scroll-margin-top: 72px;
  padding: 88px 0;
  background: #f7f9fb;

  @media (max-width: 760px) {
    scroll-margin-top: 64px;
    padding: 48px 0;
  }
`

const PortfolioContainer = styled.div`
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;

  @media (max-width: 760px) {
    width: calc(100% - 32px);
  }
`

const PortfolioTitle = styled.div`
  max-width: 760px;
  margin-bottom: 34px;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    color: #0f7f6c;
    background: rgba(115, 223, 196, 0.18);
    border: 1px solid rgba(15, 127, 108, 0.22);
    font-size: 14px;
    font-weight: 800;
  }

  h2 {
    margin: 14px 0 0;
    color: #152236;
    font-size: 34px;
    line-height: 1.25;
    letter-spacing: 0;
  }

  p {
    margin: 14px 0 0;
    color: #5b697c;
    font-size: 17px;
    line-height: 1.7;
  }

  @media (max-width: 760px) {
    margin-bottom: 24px;

    .eyebrow {
      min-height: 28px;
      padding: 0 10px;
      font-size: 12px;
    }

    h2 {
      margin-top: 10px;
      font-size: 23px;
      line-height: 1.32;
    }

    p {
      margin-top: 10px;
      font-size: 14px;
      line-height: 1.65;
    }
  }
`

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
`

const PortfolioItem = styled.button`
  width: 100%;
  padding: 0;
  border: 1px solid #e4eaf0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 12px 32px rgba(17, 34, 51, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(115, 223, 196, 0.7);
    box-shadow: 0 20px 44px rgba(17, 34, 51, 0.1);
  }
`

const PortfolioImage = styled.div`
  aspect-ratio: 16 / 11;
  padding: 18px;
  background: #eef3f2;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 560px) {
    aspect-ratio: 2075 / 1176;
    padding: 0;
    overflow: hidden;

    img {
      height: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`

const PortfolioBody = styled.div`
  padding: 18px 18px 20px;

  h3 {
    margin: 0;
    color: #152236;
    font-size: 18px;
    line-height: 1.35;
  }

  p {
    margin: 8px 0 0;
    color: #607086;
    font-size: 14px;
    line-height: 1.55;
  }

  @media (max-width: 560px) {
    min-height: 76px;
    padding: 11px 12px;

    h3 {
      font-size: 14px;
      line-height: 1.35;
    }

    p {
      margin-top: 5px;
      font-size: 12px;
      line-height: 1.4;
    }
  }
`

interface RevealContainerProps {
  visible: boolean
  delay: number
}

const RevealContainer = styled.div<RevealContainerProps>`
  width: 100%;
  height: 100%;
  opacity: ${ props => props.visible ? 1 : 0 };
  transform: translateY(${ props => props.visible ? '0' : '22px' });
  transition: opacity 0.55s ease, transform 0.55s ease;
  transition-delay: ${ props => props.delay }ms;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

interface RevealProps {
  children: React.ReactNode
  delay?: number
}

function Reveal(props: RevealProps) {
  const { children, delay = 0, } = props
  const [ visible, setVisible, ] = useState(false)
  const element = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = element.current

    if(!node) {
      return
    }

    if(!('IntersectionObserver' in window)) {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver((entries) => {
      if(entries[0].isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, {
      rootMargin: '0px 0px -80px 0px',
      threshold: 0.16,
    })

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return (
    <RevealContainer ref={ element } visible={ visible } delay={ delay }>
      { children }
    </RevealContainer>
  )
}

const PORTFOLIOS: Array<Portfolio> = [
  {
    name: '마녀의 빗자루',
    description: '청소 중개 웹 서비스',
    thumbnailImage: require('./assets/images/mabit.png'),
  },
  {
    name: 'Fasket',
    description: '우리 동네 배달 마켓 앱 서비스',
    thumbnailImage: require('./assets/images/fasket.png'),
  },
  {
    name: 'Hey! Mirror',
    description: '모바일 데이팅 서비스',
    thumbnailImage: require('./assets/images/hey_mirror.png'),
  },
  {
    name: '폐업119',
    description: '폐업을 위한 올인원 앱 플랫폼',
    thumbnailImage: require('./assets/images/close119.png'),
  },
  {
    name: '블리스 다이어트',
    description: '오프라인 피트니스 센터 회원관리 앱',
    thumbnailImage: require('./assets/images/blissdiet.png'),
  },
  {
    name: 'Tadoo',
    description: '오픈마켓 형태의 재능 거래 모바일 플랫폼',
    thumbnailImage: require('./assets/images/tadoo.png'),
  },
  {
    name: 'HKTimes',
    description: '홍콩 최신 뉴스 기반 커뮤니티 앱',
    thumbnailImage: require('./assets/images/hktimes.png'),
  },
  {
    name: 'GnB Book Pi',
    description: '영어학습을 위한 E-Book 앱',
    thumbnailImage: require('./assets/images/gnbbookpi.png'),
  },
  {
    name: 'Plezus',
    description: '게이머를 위한 SNS',
    thumbnailImage: require('./assets/images/plezus.png'),
  },
  /*
  {
    name: 'YOLotto',
    description: '블록체인 기반 로또 및 ICO 시스템',
    thumbnailImage: require('./assets/images/yolotto.png'),
  },
 */
  {
    name: '러브블라썸',
    description: '결혼/연애 만남을 위한 웹 플랫폼',
    thumbnailImage: require('./assets/images/loveblossom.jpg'),
  },
]

const PORTFOLIOS_MOBILE: Array<Portfolio> = [
  {
    name: '마녀의 빗자루',
    description: '청소 중개 웹 서비스',
    thumbnailImage: require('./assets/images/mabit.png'),
  },
  {
    name: 'Fasket',
    description: '우리 동네 배달 마켓 앱 서비스',
    thumbnailImage: require('./assets/images/fasket.png'),
  },
  {
    name: 'Hey! Mirror',
    description: '모바일 데이팅 서비스',
    thumbnailImage: require('./assets/images/hey_mirror.png'),
  },
  {
    name: '폐업119',
    description: '폐업을 위한 올인원 앱 플랫폼',
    thumbnailImage: require('./assets/images/close119.png'),
  },
  {
    name: '블리스 다이어트',
    description: '오프라인 피트니스 센터 회원관리 앱',
    thumbnailImage: require('./assets/images/blissdiet.png'),
  },
  {
    name: 'Tadoo',
    description: '오픈마켓 형태의 재능 거래 모바일 플랫폼',
    thumbnailImage: require('./assets/images/tadoo.png'),
  },
  {
    name: 'HKTimes',
    description: '홍콩 최신 뉴스 기반 커뮤니티 앱',
    thumbnailImage: require('./assets/images/hktimes.png'),
  },
  {
    name: 'GnB Book Pi',
    description: '영어학습을 위한 E-Book 앱',
    thumbnailImage: require('./assets/images/gnbbookpi.png'),
  },
  {
    name: 'Plezus',
    description: '게이머를 위한 SNS',
    thumbnailImage: require('./assets/images/plezus.png'),
  },
  /*
  {
    name: 'YOLotto',
    description: '블록체인 기반 로또 및 ICO 시스템',
    thumbnailImage: require('./assets/images/yolotto.png'),
  },
   */
]

function Section3(props: Props) {
  const [ isImageViewerOpen, setImageViewerOpen, ] = useState(false)
  const [ imageViewerIndex, setImageViewerIndex, ] = useState(0)

  const isSmall = useMediaQuery({ query: '(max-width: 849px)', })
  const portfolios = isSmall ? PORTFOLIOS_MOBILE : PORTFOLIOS

  const onPortfolioCardClick = (portfolio: Portfolio, index: number) => {
    return () => {
      setImageViewerIndex(index)
      setImageViewerOpen(true)

      ReactPixel.trackCustom('view_portfolio_click', {
        name: portfolio.name,
      })
    }
  }

  return (
    <Section3Container { ... props }>
      {
        isImageViewerOpen && (
          <ImageViewer
            disableScroll={ true }
            currentIndex={ imageViewerIndex }
            closeOnClickOutside={ true }
            onClose={ () => setImageViewerOpen(false) }
            src={
              portfolios.map(portfolio => portfolio.thumbnailImage)
            }
          />
        )
      }
      <PortfolioContainer>
        <Reveal>
          <PortfolioTitle>
            <div className='eyebrow'>Portfolio</div>
            <h2>실제로 만들어온 서비스들입니다.</h2>
            <p>
              다양한 단계의 서비스와 운영 도구를 함께 만들었습니다. 이미지를 눌러 작업 화면을 확인할 수 있습니다.
            </p>
          </PortfolioTitle>
        </Reveal>
        <PortfolioGrid>
        {
          portfolios.map((portfolio, index) => (
            <Reveal key={ portfolio.name } delay={ index * 45 }>
              <PortfolioItem
                type='button'
                onClick={ onPortfolioCardClick(portfolio, index) }>
                <PortfolioImage>
                  <img src={ portfolio.thumbnailImage } alt={ portfolio.name } />
                </PortfolioImage>
                <PortfolioBody>
                  <h3>{ portfolio.name }</h3>
                  <p>{ portfolio.description }</p>
                </PortfolioBody>
              </PortfolioItem>
            </Reveal>
          ))
        }
        </PortfolioGrid>
      </PortfolioContainer>
    </Section3Container>
  )
}

export default Section3
